export const accreditation = {
  standard: "ISO/IEC 17025:2017",
  certificateNumber: "NABLC0626MH04748",
  validFrom: "06/06/2026",
  validUntil: "05/06/2030",
};

const calibrationProcess = [
  {
    title: "Instrument Inspection",
    text: "Every instrument is inspected for physical condition, cleanliness, functionality, and measurement capability before calibration begins.",
  },
  {
    title: "Calibration Using Certified Standards",
    text: "Our engineers compare the instrument with certified reference standards using internationally accepted calibration procedures.",
  },
  {
    title: "Measurement Analysis",
    text: "Readings are recorded and analyzed to determine accuracy, deviation, repeatability, and measurement uncertainty.",
  },
  {
    title: "Adjustment (Where Applicable)",
    text: "When permitted and required, adjustments are made to restore the instrument within acceptable tolerance limits.",
  },
  {
    title: "Final Verification",
    text: "After calibration or adjustment, the instrument is tested again to verify its accuracy and performance.",
  },
  {
    title: "Calibration Certificate",
    text: "A detailed calibration certificate is issued with measurement results, uncertainty values, traceability information, and compliance details.",
  },
];

const commonAdvantages = [
  "ISO/IEC 17025:2017 accredited laboratory",
  "Experienced calibration engineers",
  "Advanced calibration equipment",
  "National and international traceability",
  "Fast turnaround time",
  "Laboratory and on-site calibration services",
  "Detailed calibration certificates",
  "Competitive pricing",
  "PAN India service coverage",
  "Dedicated technical support",
];

const makeContactSection = (serviceName, measurementGoal) => ({
  title: "Contact Recon Calibration & Test Labs LLP",
  paragraphs: [
    `Looking for trusted ${serviceName} in Pune or anywhere in India?`,
    `Contact Recon Calibration & Test Labs LLP today for ISO/IEC 17025:2017 accredited ${serviceName}. Our experienced professionals are ready to help you ${measurementGoal}.`,
  ],
  cta: true,
});

export const homeOnPageContent = {
  title:
    "Calibration Services in Pune | Calibration Services in India | Recon Calibration & Test Labs LLP",
  subtitle: "ISO/IEC 17025:2017 Accredited Calibration Services You Can Count On",
  intro: [
    "Recon Calibration & Test Labs LLP is one of the leading providers of calibration services in Pune and calibration services in India. Established in 2019, we have built a strong reputation for delivering accurate, reliable, and NABL-accredited calibration solutions that help industries maintain precision, compliance, and product quality.",
    `As an ${accreditation.standard} accredited calibration laboratory (NABL Certificate No. ${accreditation.certificateNumber}), we understand that precise measurements are essential for quality assurance, operational efficiency, and regulatory compliance. Every calibration is performed using internationally traceable reference standards and follows documented quality procedures to ensure dependable and repeatable results.`,
    "Whether you require laboratory calibration or on-site calibration, our experienced engineers deliver fast turnaround times, technical expertise, and complete measurement traceability to support your business operations.",
  ],
  sections: [
    {
      title: "Complete Calibration Services for Every Industry",
      paragraphs: [
        "Recon Calibration & Test Labs LLP offers comprehensive calibration services for businesses across India. We support manufacturing, pharmaceutical, automotive, healthcare, laboratory, food processing, educational, research, and engineering organizations.",
        "Our calibration capabilities include:",
      ],
      items: [
        "Electrical Calibration",
        "Pressure Calibration",
        "Dimensional Calibration",
        "Mass & Volume Calibration",
        "Thermal Calibration",
        "Force & Hardness Calibration",
      ],
      footer:
        "Every certificate includes measurement results, measurement uncertainty, traceability details, standards used, and NABL accreditation details to support ISO systems, customer audits, and regulatory requirements.",
    },
    {
      title: "Calibration Services in Pune Trusted by Industries",
      paragraphs: [
        "Businesses searching for dependable calibration services in Pune choose Recon Calibration & Test Labs LLP because of our commitment to precision, reliability, and customer satisfaction.",
        "Even a small measurement error can cause product failures, production losses, compliance issues, higher operational costs, and customer complaints.",
        "Our engineers use advanced equipment maintained to international standards. For equipment that cannot be transported easily, we provide professional on-site calibration to minimize downtime.",
      ],
    },
    {
      title: "Calibration Services in India with Nationwide Support",
      paragraphs: [
        "Recon Calibration & Test Labs LLP delivers consistent calibration services across India for single-site businesses and organizations operating multiple facilities.",
        "We continuously invest in advanced calibration equipment, engineer training, quality management systems, and international best practices.",
      ],
      items: [
        "Measurement Accuracy",
        "Product Quality",
        "Equipment Reliability",
        "Workplace Safety",
        "Regulatory Compliance",
      ],
    },
    {
      title: "Why Choose Recon Calibration & Test Labs LLP?",
      paragraphs: [
        "We provide calibration services backed by technical expertise and customer-focused support. Our goal is to improve measurement confidence, reduce uncertainty, extend equipment life, and maintain compliance.",
      ],
      items: [
        `ISO/IEC 17025:2017 NABL accredited laboratory - Certificate ${accreditation.certificateNumber}`,
        "Experienced calibration engineers",
        "Fast turnaround and reliable results",
        "National and international traceability",
        "Laboratory and on-site calibration",
        "Modern calibration equipment",
        "Competitive and customized solutions",
        "Nationwide service coverage",
        "Responsive customer support",
      ],
    },
    {
      title: "Our Accreditation",
      paragraphs: [
        `Recon Calibration & Test Labs LLP is an ${accreditation.standard} accredited calibration laboratory certified by NABL under Certificate ${accreditation.certificateNumber}, valid from ${accreditation.validFrom} to ${accreditation.validUntil}.`,
        "Our accreditation demonstrates technical competence, a documented quality management system, and the ability to provide reliable calibration in accordance with international standards.",
      ],
    },
    {
      title: "Our Calibration Process",
      steps: [
        {
          title: "Get Started",
          text: "Contact our calibration experts to discuss your instruments, scope, and preferred schedule.",
        },
        {
          title: "Equipment Preparation",
          text: "We select reference standards with the resolution and capability required for the equipment being tested.",
        },
        {
          title: "Calibration & Testing",
          text: "Our engineers calibrate each instrument using ISO/IEC 17025:2017 procedures and internationally traceable standards.",
        },
        {
          title: "Receive Results",
          text: "You receive a detailed calibration certificate with results, uncertainty calculations, and traceability information.",
        },
      ],
    },
    {
      title: "Industries We Serve",
      paragraphs: [
        "We provide calibration services tailored to the operational requirements of organizations of every size.",
      ],
      items: [
        "Pharmaceutical Manufacturing",
        "Automotive Industry",
        "Aerospace",
        "Food & Beverage",
        "Hospitals & Healthcare",
        "Testing Laboratories",
        "Engineering Industries",
        "Educational Institutions",
        "Electronics Manufacturing",
        "Power & Energy",
        "Research & Development",
        "Process Industries",
      ],
    },
    {
      title: "Our Commitment to Quality",
      paragraphs: [
        "Quality forms the foundation of everything we do. Every calibration follows documented procedures, international standards, and strict laboratory controls.",
        "Our laboratory environment is monitored for accurate and repeatable measurements, and our engineers receive ongoing technical training.",
        "Our services help reduce measurement uncertainty, improve product quality, prevent costly downtime, and maintain regulatory compliance.",
      ],
    },
    {
      title: "Partner with Recon Calibration & Test Labs LLP",
      paragraphs: [
        "If you are searching for trusted calibration services in Pune or dependable calibration services in India, Recon Calibration & Test Labs LLP is your reliable calibration partner.",
        "Contact us to discuss your calibration requirements and experience solutions designed for accuracy, reliability, and long-term performance.",
      ],
      cta: true,
    },
  ],
  faqs: [
    {
      question: "What calibration services does Recon Calibration & Test Labs LLP provide?",
      answer:
        "We provide electrical, pressure, thermal, dimensional, mass and volume, force and hardness, laboratory, and on-site calibration services.",
    },
    {
      question: "Is Recon Calibration & Test Labs LLP NABL accredited?",
      answer: `Yes. We are an ISO/IEC 17025:2017 NABL accredited calibration laboratory under Certificate ${accreditation.certificateNumber}.`,
    },
    {
      question: "Do you provide calibration services in Pune?",
      answer:
        "Yes. We provide professional calibration services in Pune for industries, laboratories, manufacturers, hospitals, and research organizations.",
    },
    {
      question: "Do you offer calibration services across India?",
      answer:
        "Yes. We provide nationwide support for businesses operating at single or multiple locations across India.",
    },
    {
      question: "Why is calibration important?",
      answer:
        "Calibration supports accurate measurements, product quality, equipment reliability, lower production risk, and compliance with quality standards.",
    },
    {
      question: "Do you provide on-site calibration?",
      answer:
        "Yes. We provide on-site calibration for eligible equipment that cannot be transported easily to our laboratory.",
    },
    {
      question: "Will I receive a calibration certificate?",
      answer:
        "Yes. Every completed calibration includes a detailed certificate with results, uncertainty calculations, and traceability information.",
    },
    {
      question: "Which industries do you serve?",
      answer:
        "We serve pharmaceutical, automotive, aerospace, healthcare, food processing, engineering, electronics, power, education, research, and manufacturing organizations.",
    },
  ],
  testimonials: [
    {
      quote:
        "Recon Calibration & Test Labs LLP consistently delivers accurate calibration with quick turnaround. Their NABL-accredited services have helped us maintain compliance and improve operational efficiency.",
      attribution: "Manufacturing Industry, Pune",
    },
    {
      quote:
        "Excellent support and professional engineers. Their on-site calibration service saved us valuable production time.",
      attribution: "Pharmaceutical Company",
    },
    {
      quote:
        "Highly recommended for reliable calibration services in India. Their reports are detailed, accurate, and always delivered on time.",
      attribution: "Engineering Company",
    },
  ],
};

export const serviceOnPageContent = {
  "electrical-calibration": {
    pageTitle:
      "Electrical Calibration Services in Pune | Electrical Calibration Services in India",
    subtitle: "Accurate & NABL Accredited Electrical Calibration Services",
    metaTitle:
      "Electrical Calibration Services in Pune | Electrical Calibration Services in India | Recon Calibration",
    metaDescription:
      "Get ISO/IEC 17025:2017 NABL accredited Electrical Calibration Services in Pune and across India. Accurate, reliable, and traceable electrical calibration solutions.",
    ogTitle:
      "Electrical Calibration Services in Pune | Recon Calibration & Test Labs LLP",
    ogDescription:
      "Get ISO/IEC 17025:2017 NABL accredited Electrical Calibration Services in Pune and across India.",
    twitterTitle: "Electrical Calibration Services in Pune",
    twitterDescription:
      "Professional ISO/IEC 17025:2017 accredited Electrical Calibration Services in Pune and across India.",
    intro: [
      "Recon Calibration & Test Labs LLP offers reliable and accurate Electrical Calibration Services for a wide range of electrical measuring instruments used across industries. As an ISO/IEC 17025:2017 accredited laboratory, we help organizations maintain measurement accuracy, equipment performance, and compliance.",
      "Whether you require Electrical Calibration Services in Pune or anywhere across India, our engineers calibrate instruments using advanced reference standards with complete traceability.",
    ],
    sections: [
      {
        title: "What is Electrical Calibration?",
        paragraphs: [
          "Electrical calibration verifies and, where applicable, adjusts electrical measuring instruments by comparing them with certified reference standards. Regular calibration corrects drift caused by use, environmental conditions, and aging components.",
          "It supports product quality, workplace safety, lower operational risk, and compliance with industry requirements.",
        ],
      },
      {
        title: "Our Electrical Calibration Services",
        paragraphs: [
          "We provide comprehensive calibration for electrical measuring instruments, including:",
        ],
        items: [
          "Digital Multimeters",
          "Clamp Meters",
          "Insulation Testers (Meggers)",
          "Earth Testers",
          "Power Meters",
          "Energy Meters",
          "AC/DC Voltage and Current Sources",
          "Oscilloscopes",
          "Function Generators",
          "Frequency Counters",
          "Process and Loop Calibrators",
          "Digital Panel Meters",
          "Temperature Indicators",
          "Data Loggers",
          "Tachometers",
          "Power Supplies",
          "Electrical Safety Testers",
          "Milliohm Meters",
        ],
        footer:
          "All calibrations use certified standards traceable to national and international standards.",
      },
      {
        title: "Why Choose Our Electrical Calibration Services?",
        paragraphs: [
          "Organizations choose us for reliable, accurate, and timely electrical calibration that supports compliance and operational efficiency.",
        ],
        items: commonAdvantages,
      },
      {
        title: "Industries We Serve",
        items: [
          "Manufacturing",
          "Pharmaceuticals",
          "Automotive",
          "Aerospace & Aviation",
          "Electrical & Electronics",
          "Food & Beverage",
          "Chemical Processing",
          "Power Generation & Distribution",
          "Oil & Gas",
          "Hospitals & Healthcare",
          "Research and Educational Institutions",
          "Engineering and Testing Laboratories",
        ],
      },
      { title: "Our Electrical Calibration Process", steps: calibrationProcess },
      {
        title: "Benefits of Regular Electrical Calibration",
        items: [
          "Improved Measurement Accuracy",
          "Enhanced Product Quality",
          "Reduced Equipment Downtime",
          "Compliance with ISO and Industry Standards",
          "Increased Workplace Safety",
          "Better Process Control",
          "Lower Maintenance Costs",
          "Reduced Measurement Risk",
          "Increased Equipment Reliability",
          "Improved Customer Confidence",
        ],
      },
      {
        title: "Electrical Calibration Services in Pune",
        paragraphs: [
          "We support manufacturers, laboratories, service providers, and industrial facilities in Pune with fast, accurate laboratory and on-site electrical calibration services.",
        ],
      },
      {
        title: "Electrical Calibration Services Across India",
        paragraphs: [
          "Our PAN India service capability supports individual instruments and complete facilities with consistent quality, technical expertise, and cost-effective calibration.",
        ],
      },
      makeContactSection(
        "Electrical Calibration Services",
        "maintain measurement accuracy, regulatory compliance, and reliable electrical instruments"
      ),
    ],
    faqs: [
      {
        question: "What is Electrical Calibration?",
        answer:
          "Electrical calibration compares electrical measuring instruments with certified reference standards to verify and maintain measurement accuracy.",
      },
      {
        question: "Why are Electrical Calibration Services important?",
        answer:
          "They minimize measurement errors, improve product quality, reduce downtime, support safety, and maintain ISO and industry compliance.",
      },
      {
        question: "Which electrical instruments can you calibrate?",
        answer:
          "We calibrate multimeters, clamp meters, insulation and earth testers, power and energy meters, oscilloscopes, generators, counters, calibrators, power supplies, panel meters, and related electrical instruments.",
      },
      {
        question: "Are your Electrical Calibration Services NABL accredited?",
        answer: `Yes. Recon Calibration & Test Labs LLP is ISO/IEC 17025:2017 NABL accredited under Certificate ${accreditation.certificateNumber}.`,
      },
      {
        question: "How often should electrical instruments be calibrated?",
        answer:
          "Many organizations use a 6 to 12 month interval, adjusted for instrument type, usage, environment, risk, and quality-system requirements.",
      },
      {
        question: "Do you provide Electrical Calibration Services in Pune and across India?",
        answer:
          "Yes. We provide laboratory and eligible on-site Electrical Calibration Services in Pune and nationwide.",
      },
      {
        question: "What industries require Electrical Calibration Services?",
        answer:
          "These services are used across manufacturing, pharmaceutical, automotive, aerospace, electronics, food, chemical, oil and gas, power, healthcare, education, research, and testing organizations.",
      },
      {
        question:
          "What are the benefits of ISO/IEC 17025 accredited Electrical Calibration Services?",
        answer:
          "Accredited calibration provides technically valid results, documented measurement uncertainty, traceability to national or international standards, and confidence that the laboratory operates under a recognized quality system.",
      },
      {
        question: "Do you provide on-site Electrical Calibration Services?",
        answer:
          "Yes. Eligible instruments can be calibrated at the customer site to reduce downtime, with laboratory calibration available when controlled conditions or specialized standards are required.",
      },
      {
        question:
          "Why choose Recon Calibration & Test Labs LLP for Electrical Calibration?",
        answer:
          "Choose Recon for ISO/IEC 17025:2017 NABL accreditation, experienced engineers, traceable standards, complete documentation, laboratory and eligible on-site support, and dependable service across India.",
      },
    ],
  },

  "dimensional-calibration": {
    pageTitle:
      "Dimensional Calibration Services in Pune | Dimensional Calibration Services in India",
    subtitle:
      "Accurate & ISO/IEC 17025:2017 Accredited Dimensional Calibration Services",
    metaTitle:
      "Dimensional Calibration Services in Pune | Dimensional Calibration Services in India | Recon Calibration",
    metaDescription:
      "ISO/IEC 17025:2017 accredited Dimensional Calibration Services in Pune and across India for precision measuring instruments.",
    ogTitle:
      "Dimensional Calibration Services in Pune | Recon Calibration & Test Labs LLP",
    ogDescription:
      "Get ISO/IEC 17025:2017 NABL accredited Dimensional Calibration Services in Pune and across India.",
    twitterTitle: "Dimensional Calibration Services in Pune",
    twitterDescription:
      "Professional ISO/IEC 17025:2017 accredited Dimensional Calibration Services in Pune and across India.",
    intro: [
      "Recon Calibration & Test Labs LLP provides precise and reliable Dimensional Calibration Services in Pune and across India for instruments used in manufacturing, automotive, aerospace, engineering, pharmaceuticals, and quality control.",
      "We use advanced calibration equipment and traceable reference standards to support product quality, manufacturing precision, and compliance.",
    ],
    sections: [
      {
        title: "What is Dimensional Calibration?",
        paragraphs: [
          "Dimensional calibration verifies instruments used to measure length, height, width, diameter, thickness, depth, and other physical dimensions against certified reference standards.",
          "Regular calibration reduces uncertainty, manufacturing errors, product rejection, and equipment-related quality risk.",
        ],
      },
      {
        title: "Our Dimensional Calibration Services",
        items: [
          "Vernier Calipers",
          "Outside, Inside and Depth Micrometers",
          "Height Gauges",
          "Dial Gauges and Indicators",
          "Bore Gauges",
          "Slip Gauges (Gauge Blocks)",
          "Feeler Gauges",
          "Plug, Ring and Thread Gauges",
          "Radius Gauges",
          "Bevel Protractors",
          "Steel Rules and Measuring Tapes",
          "Surface Plates",
          "Thickness Gauges",
          "Combination Sets",
          "Precision Squares",
          "Bench Centres",
        ],
        footer:
          "Calibration is performed using traceable master standards in a controlled environment for accurate, repeatable results.",
      },
      {
        title: "Why Choose Recon Calibration & Test Labs LLP?",
        items: commonAdvantages,
      },
      {
        title: "Industries We Serve",
        items: [
          "Manufacturing",
          "Automotive & Auto Components",
          "Aerospace & Aviation",
          "Precision and Heavy Engineering",
          "Tool Rooms",
          "Pharmaceutical Manufacturing",
          "Electrical and Electronics",
          "Metal Fabrication",
          "Plastic and Rubber",
          "Research and Development",
          "Education",
          "Quality Control, Testing and Inspection",
        ],
      },
      { title: "Our Dimensional Calibration Process", steps: calibrationProcess },
      {
        title: "Benefits of Regular Dimensional Calibration",
        items: [
          "Improved Measurement Accuracy",
          "Enhanced Product Quality",
          "Reduced Manufacturing Defects",
          "Compliance with ISO and Industry Standards",
          "Increased Equipment Reliability",
          "Better Process Control",
          "Reduced Production Costs",
          "Lower Product-Rejection Risk",
          "Improved Customer Satisfaction",
          "Extended Instrument Life",
        ],
      },
      {
        title: "Dimensional Calibration Services in Pune",
        paragraphs: [
          "Our Pune laboratory supports manufacturers, engineering companies, laboratories, and industrial organizations with precise calibration and quick turnaround. On-site service is available for selected instruments.",
        ],
      },
      {
        title: "Dimensional Calibration Services Across India",
        paragraphs: [
          "We provide dependable nationwide support for periodic calibration, preventive maintenance, and calibration-management requirements.",
        ],
      },
      makeContactSection(
        "Dimensional Calibration Services",
        "maintain dimensional accuracy, improve product quality, and meet national and international standards"
      ),
    ],
    faqs: [
      {
        question: "What is Dimensional Calibration?",
        answer:
          "Dimensional calibration verifies precision measuring instruments against certified reference standards.",
      },
      {
        question: "Which dimensional measuring instruments do you calibrate?",
        answer:
          "We calibrate calipers, micrometers, height and dial gauges, bore gauges, gauge blocks, plug, ring and thread gauges, rules, tapes, surface plates, and other precision instruments.",
      },
      {
        question: "Are your Dimensional Calibration Services NABL accredited?",
        answer: `Yes. We are ISO/IEC 17025:2017 NABL accredited under Certificate ${accreditation.certificateNumber}.`,
      },
      {
        question: "Do you provide Dimensional Calibration Services in Pune?",
        answer:
          "Yes. We provide accredited laboratory calibration in Pune and on-site service for eligible dimensional measuring instruments.",
      },
      {
        question: "Do you provide Dimensional Calibration Services across India?",
        answer:
          "Yes. We support customers across India with dependable logistics and on-site calibration for eligible instruments.",
      },
      {
        question: "Why is Dimensional Calibration important?",
        answer:
          "It improves measurement accuracy, reduces defects, supports ISO compliance, and enhances product quality.",
      },
      {
        question: "How often should dimensional instruments be calibrated?",
        answer:
          "A 6 to 12 month interval is common, but the correct interval depends on usage, conditions, risk, and quality requirements.",
      },
      {
        question: "Do you provide calibration certificates?",
        answer:
          "Yes. Each completed calibration includes results, uncertainty, traceability, and compliance information.",
      },
    ],
  },

  "pressure-calibration": {
    pageTitle:
      "Pressure Calibration Services in Pune | Pressure Calibration Services in India",
    subtitle:
      "Accurate & ISO/IEC 17025:2017 Accredited Pressure Calibration Services",
    metaTitle:
      "Pressure Calibration Services in Pune | Pressure Calibration Services in India | Recon Calibration",
    metaDescription:
      "Get ISO/IEC 17025:2017 accredited Pressure Calibration Services in Pune and across India. Accurate, traceable calibration for pressure measuring instruments.",
    ogTitle:
      "Pressure Calibration Services in Pune | Recon Calibration & Test Labs LLP",
    ogDescription:
      "Get ISO/IEC 17025:2017 NABL accredited Pressure Calibration Services in Pune and across India.",
    twitterTitle: "Pressure Calibration Services in Pune",
    twitterDescription:
      "Professional ISO/IEC 17025:2017 accredited Pressure Calibration Services in Pune and across India.",
    intro: [
      "Recon Calibration & Test Labs LLP provides accurate, reliable, and traceable Pressure Calibration Services in Pune and across India for industrial pressure instruments.",
      "Precise pressure measurement supports product quality, operational safety, and regulatory compliance. Our engineers use advanced equipment and internationally traceable reference standards.",
    ],
    sections: [
      {
        title: "What is Pressure Calibration?",
        paragraphs: [
          "Pressure calibration compares an instrument with a certified reference standard to verify accuracy and, where applicable, correct deviation.",
          "Regular calibration supports process accuracy, equipment reliability, lower operational risk, and compliance with ISO, GMP, and industry systems.",
        ],
      },
      {
        title: "Our Pressure Calibration Services",
        items: [
          "Pressure and Digital Pressure Gauges",
          "Vacuum and Compound Gauges",
          "Pressure and Differential Pressure Transmitters",
          "Differential Pressure Gauges",
          "Pressure Switches",
          "Pressure Indicators and Recorders",
          "Pressure Calibrators",
          "Digital Manometers",
          "Vacuum Transmitters",
          "Dead Weight Testers",
          "Hydraulic and Pneumatic Pressure Instruments",
          "Pressure Sensors and Controllers",
          "Process Pressure Instruments",
        ],
      },
      { title: "Why Choose Recon Calibration & Test Labs LLP?", items: commonAdvantages },
      {
        title: "Industries We Serve",
        items: [
          "Manufacturing",
          "Pharmaceuticals",
          "Chemical Processing",
          "Oil & Gas",
          "Power Generation",
          "Food & Beverage",
          "Automotive",
          "Aerospace & Aviation",
          "Water Treatment",
          "HVAC",
          "Hospitals & Healthcare",
          "Research, Engineering, Testing and Inspection",
        ],
      },
      { title: "Our Pressure Calibration Process", steps: calibrationProcess },
      {
        title: "Benefits of Regular Pressure Calibration",
        items: [
          "Accurate Pressure Measurements",
          "Improved Product Quality",
          "Enhanced Workplace Safety",
          "Compliance with ISO and Regulatory Standards",
          "Reduced Equipment Downtime",
          "Increased Equipment Reliability",
          "Lower Maintenance Costs",
          "Better Process Control",
          "Reduced Production-Failure Risk",
          "Extended Instrument Life",
        ],
      },
      {
        title: "Pressure Calibration Services in Pune",
        paragraphs: [
          "We support manufacturing plants, pharmaceutical companies, engineering organizations, laboratories, and industrial facilities in Pune with laboratory and eligible on-site pressure calibration.",
        ],
      },
      {
        title: "Pressure Calibration Services Across India",
        paragraphs: [
          "Our nationwide service supports single pressure gauges and complete plant instrumentation with consistent quality and complete traceability.",
        ],
      },
      makeContactSection(
        "Pressure Calibration Services",
        "maintain pressure accuracy, improve equipment performance, and meet industry requirements"
      ),
    ],
    faqs: [
      {
        question: "What is pressure calibration?",
        answer:
          "Pressure calibration compares a pressure instrument with a certified reference standard to verify and maintain accuracy.",
      },
      {
        question: "Why is pressure calibration important?",
        answer:
          "It improves accuracy, product quality, safety, equipment reliability, and compliance while reducing process risk.",
      },
      {
        question: "Which instruments do you calibrate?",
        answer:
          "We calibrate pressure and vacuum gauges, transmitters, switches, differential instruments, manometers, sensors, controllers, dead weight testers, and related pressure instruments.",
      },
      {
        question: "How often should pressure instruments be calibrated?",
        answer:
          "Many instruments are calibrated every 6 to 12 months, depending on usage, environment, manufacturer guidance, risk, and quality-system requirements.",
      },
      {
        question: "Do you provide on-site pressure calibration services?",
        answer:
          "Yes. We offer laboratory and eligible on-site pressure calibration to reduce downtime.",
      },
      {
        question: "Do you issue a calibration certificate?",
        answer:
          "Yes. Each completed calibration includes results, uncertainty, traceability information, and compliance status.",
      },
      {
        question: "Do you provide pressure calibration services across India?",
        answer:
          "Yes. We serve customers across India through laboratory and on-site calibration solutions.",
      },
      {
        question: "Is your laboratory ISO/IEC 17025:2017 accredited?",
        answer: `Yes. Recon Calibration & Test Labs LLP is accredited under NABL Certificate ${accreditation.certificateNumber}.`,
      },
      {
        question: "Which industries use Pressure Calibration Services?",
        answer:
          "Pressure calibration supports manufacturing, pharmaceutical, chemical, oil and gas, power, automotive, aerospace, food, healthcare, water treatment, HVAC, laboratories, and testing organizations.",
      },
      {
        question:
          "Why choose Recon Calibration & Test Labs LLP for Pressure Calibration?",
        answer:
          "Recon combines ISO/IEC 17025:2017 NABL accreditation, traceable reference standards, experienced engineers, documented uncertainty, laboratory and eligible on-site support, and nationwide service.",
      },
    ],
  },

  "mass-volume-calibration": {
    pageTitle:
      "Mass & Volume Calibration Services in Pune | Mass & Volume Calibration Services in India",
    subtitle:
      "Accurate & ISO/IEC 17025:2017 Accredited Mass & Volume Calibration Services",
    metaTitle:
      "Mass & Volume Calibration Services in Pune | Mass & Volume Calibration Services in India | Recon Calibration",
    metaDescription:
      "Get ISO/IEC 17025:2017 accredited Mass & Volume Calibration Services in Pune and across India for weighing and volumetric measuring instruments.",
    ogTitle:
      "Mass & Volume Calibration Services in Pune | Recon Calibration & Test Labs LLP",
    ogDescription:
      "Get ISO/IEC 17025:2017 NABL accredited Mass & Volume Calibration Services in Pune and across India.",
    twitterTitle: "Mass & Volume Calibration Services in Pune",
    twitterDescription:
      "Professional ISO/IEC 17025:2017 accredited Mass & Volume Calibration Services in Pune and across India.",
    intro: [
      "Recon Calibration & Test Labs LLP provides precise and reliable Mass & Volume Calibration Services in Pune and across India for laboratories, manufacturing, pharmaceuticals, food processing, chemicals, healthcare, and research.",
      "Accurate mass and volume measurements support product quality, process consistency, inventory control, and regulatory compliance.",
    ],
    sections: [
      {
        title: "What is Mass & Volume Calibration?",
        paragraphs: [
          "Mass & Volume Calibration verifies weighing and volumetric instruments against certified reference standards to identify deviation and confirm performance within specified tolerances.",
          "Regular calibration improves accuracy, quality assurance, audit readiness, and compliance with ISO, GMP, GLP, and NABL requirements.",
        ],
      },
      {
        title: "Our Mass & Volume Calibration Services",
        groups: [
          {
            title: "Mass Calibration",
            items: [
              "Analytical, Precision and Laboratory Balances",
              "Electronic and Platform Weighing Scales",
              "Bench and Crane Scales",
              "Weighing Indicators",
              "Standard and Test Weights",
              "Industrial Weighing Systems",
            ],
          },
          {
            title: "Volume Calibration",
            items: [
              "Volumetric Flasks",
              "Pipettes and Micropipettes",
              "Burettes",
              "Measuring Cylinders",
              "Beakers",
              "Dispensers and Bottle-Top Dispensers",
              "Volumetric Pipettes",
              "Measuring Tanks",
              "Capacity Measures",
            ],
          },
        ],
      },
      { title: "Why Choose Recon Calibration & Test Labs LLP?", items: commonAdvantages },
      {
        title: "Industries We Serve",
        items: [
          "Pharmaceuticals",
          "Chemical Processing",
          "Food & Beverage",
          "Healthcare & Hospitals",
          "Research & Development",
          "Education",
          "Manufacturing",
          "Automotive",
          "Cosmetics",
          "Biotechnology",
          "Oil & Gas",
          "Testing, Environmental and Quality-Control Laboratories",
        ],
      },
      { title: "Our Mass & Volume Calibration Process", steps: calibrationProcess },
      {
        title: "Benefits of Regular Mass & Volume Calibration",
        items: [
          "Improved Measurement Accuracy",
          "Consistent Product Quality",
          "Compliance with ISO, GMP and GLP Standards",
          "Increased Equipment Reliability",
          "Reduced Measurement Errors",
          "Better Laboratory Performance",
          "Improved Production Efficiency",
          "Lower Maintenance Costs",
          "Enhanced Customer Confidence",
          "Extended Equipment Life",
        ],
      },
      {
        title: "Mass & Volume Calibration Services in Pune",
        paragraphs: [
          "We provide timely calibration for laboratories, manufacturers, pharmaceutical companies, healthcare organizations, and research institutions in Pune, including on-site service for selected equipment.",
        ],
      },
      {
        title: "Mass & Volume Calibration Services Across India",
        paragraphs: [
          "Our nationwide service supports individual balances and complete weighing and volumetric systems with accredited, traceable calibration.",
        ],
      },
      makeContactSection(
        "Mass & Volume Calibration Services",
        "achieve accurate, reliable, and traceable weighing and volumetric measurements"
      ),
    ],
    faqs: [
      {
        question: "What is Mass & Volume Calibration?",
        answer:
          "It verifies weighing and volumetric instruments against certified reference standards to ensure accurate and reliable measurements.",
      },
      {
        question: "Why is Mass & Volume Calibration important?",
        answer:
          "It improves measurement accuracy, product quality, process consistency, regulatory compliance, and equipment reliability.",
      },
      {
        question: "Which instruments do you calibrate?",
        answer:
          "We calibrate balances, weighing scales, standard weights, flasks, pipettes, micropipettes, burettes, cylinders, dispensers, and related instruments.",
      },
      {
        question: "How often should mass and volume instruments be calibrated?",
        answer:
          "A 6 to 12 month interval is common, adjusted for usage, conditions, manufacturer guidance, risk, and quality requirements.",
      },
      {
        question: "Do you provide on-site Mass & Volume Calibration Services?",
        answer:
          "Yes. We provide laboratory and on-site calibration for selected instruments.",
      },
      {
        question: "Do you provide calibration certificates?",
        answer:
          "Yes. Certificates include results, uncertainty, traceability, and compliance information.",
      },
      {
        question: "Do you provide Mass & Volume Calibration Services across India?",
        answer:
          "Yes. We support customers across India through laboratory and eligible on-site calibration.",
      },
      {
        question: "Is your laboratory ISO/IEC 17025:2017 accredited?",
        answer: `Yes. Our NABL accreditation is Certificate ${accreditation.certificateNumber}.`,
      },
      {
        question: "Which industries benefit from Mass & Volume Calibration Services?",
        answer:
          "These services benefit pharmaceutical, food and beverage, chemical, manufacturing, healthcare, biotechnology, automotive, aerospace, laboratories, research, and quality-control organizations.",
      },
      {
        question:
          "Why choose Recon Calibration & Test Labs LLP for Mass & Volume Calibration?",
        answer:
          "Recon provides ISO/IEC 17025:2017 NABL accredited results, traceable standards, experienced engineers, documented uncertainty, comprehensive certificates, and laboratory or eligible on-site support across India.",
      },
    ],
  },

  "thermal-calibration": {
    pageTitle:
      "Thermal Calibration Services in Pune | Thermal Calibration Services in India",
    subtitle:
      "Accurate & ISO/IEC 17025:2017 Accredited Thermal Calibration Services",
    metaTitle:
      "Thermal Calibration Services in Pune | Thermal Calibration Services in India | Recon Calibration",
    metaDescription:
      "Get ISO/IEC 17025:2017 accredited Thermal Calibration Services in Pune and across India for temperature measuring instruments.",
    ogTitle:
      "Thermal Calibration Services in Pune | Recon Calibration & Test Labs LLP",
    ogDescription:
      "Get ISO/IEC 17025:2017 NABL accredited Thermal Calibration Services in Pune and across India.",
    twitterTitle: "Thermal Calibration Services in Pune",
    twitterDescription:
      "Professional ISO/IEC 17025:2017 accredited Thermal Calibration Services in Pune and across India.",
    intro: [
      "Recon Calibration & Test Labs LLP provides accurate, reliable, and traceable Thermal Calibration Services in Pune and across India for temperature instruments used in manufacturing, pharmaceuticals, food, healthcare, automotive, aerospace, laboratories, and research.",
      "Accurate temperature measurement supports product quality, process efficiency, equipment safety, and compliance. Our engineers use advanced equipment and internationally traceable reference standards.",
    ],
    sections: [
      {
        title: "What is Thermal Calibration?",
        paragraphs: [
          "Thermal calibration verifies temperature measuring instruments against certified reference standards under controlled conditions to identify deviation and confirm specified tolerances.",
          "Regular calibration supports temperature control, process reliability, product quality, equipment life, and ISO, GMP, GLP, and NABL compliance.",
        ],
      },
      {
        title: "Our Thermal Calibration Services",
        items: [
          "Digital, Glass and Infrared Thermometers",
          "RTDs (Resistance Temperature Detectors)",
          "Thermocouples",
          "Temperature Indicators and Controllers",
          "Temperature Transmitters and Sensors",
          "Data Loggers",
          "Dry Block Calibrators",
          "Temperature Recorders and Switches",
          "Environmental Chambers",
          "Ovens and Furnaces",
          "Incubators",
          "Refrigerators and Freezers",
          "Autoclaves",
          "Water Baths",
        ],
      },
      { title: "Why Choose Recon Calibration & Test Labs LLP?", items: commonAdvantages },
      {
        title: "Industries We Serve",
        items: [
          "Pharmaceuticals",
          "Food & Beverage",
          "Chemical Manufacturing",
          "Automotive",
          "Aerospace & Aviation",
          "Healthcare & Hospitals",
          "Biotechnology",
          "Research & Development",
          "Electronics",
          "HVAC",
          "Power Generation",
          "Engineering, Testing and Education",
        ],
      },
      { title: "Our Thermal Calibration Process", steps: calibrationProcess },
      {
        title: "Benefits of Regular Thermal Calibration",
        items: [
          "Accurate Temperature Measurement",
          "Improved Product Quality",
          "Better Process Control",
          "Compliance with ISO, GMP and GLP Standards",
          "Reduced Product Rejections",
          "Increased Equipment Reliability",
          "Lower Maintenance Costs",
          "Enhanced Workplace Safety",
          "Improved Regulatory Compliance",
          "Extended Equipment Life",
        ],
      },
      {
        title: "Thermal Calibration Services in Pune",
        paragraphs: [
          "We support manufacturers, pharmaceutical companies, laboratories, hospitals, food processors, and engineering organizations in Pune with fast laboratory and eligible on-site calibration.",
        ],
      },
      {
        title: "Thermal Calibration Services Across India",
        paragraphs: [
          "Our nationwide services cover single thermometers and complete temperature-monitoring systems with consistent, accredited, and traceable results.",
        ],
      },
      makeContactSection(
        "Thermal Calibration Services",
        "maintain temperature accuracy, improve process efficiency, and ensure compliance"
      ),
    ],
    faqs: [
      {
        question: "What is Thermal Calibration?",
        answer:
          "Thermal calibration verifies temperature measuring instruments against certified reference standards to ensure reliable measurements.",
      },
      {
        question: "Why is Thermal Calibration important?",
        answer:
          "It supports accurate temperature control, product quality, process reliability, equipment safety, and regulatory compliance.",
      },
      {
        question: "Which instruments do you calibrate?",
        answer:
          "We calibrate thermometers, RTDs, thermocouples, indicators, controllers, transmitters, data loggers, chambers, ovens, incubators, furnaces, refrigerators, freezers, autoclaves, and related instruments.",
      },
      {
        question: "How often should temperature instruments be calibrated?",
        answer:
          "A 6 to 12 month interval is common, adjusted for usage, conditions, manufacturer guidance, risk, and quality-system requirements.",
      },
      {
        question: "Do you provide on-site Thermal Calibration Services?",
        answer:
          "Yes. We offer laboratory and on-site calibration for selected equipment.",
      },
      {
        question: "Do you provide calibration certificates?",
        answer:
          "Yes. Each completed calibration includes results, uncertainty, traceability, and compliance details.",
      },
      {
        question: "Which industries use your Thermal Calibration Services?",
        answer:
          "Our services support pharmaceutical, food, healthcare, manufacturing, biotechnology, chemical, automotive, aerospace, electronics, and research organizations.",
      },
      {
        question: "Is your laboratory ISO/IEC 17025:2017 accredited?",
        answer: `Yes. We are NABL accredited under Certificate ${accreditation.certificateNumber}.`,
      },
      {
        question: "Do you provide Thermal Calibration Services across India?",
        answer:
          "Yes. We provide nationwide laboratory and eligible on-site thermal calibration solutions.",
      },
      {
        question:
          "Why choose Recon Calibration & Test Labs LLP for Thermal Calibration?",
        answer:
          "Recon offers ISO/IEC 17025:2017 NABL accreditation, traceable temperature standards, experienced engineers, documented measurement uncertainty, laboratory and eligible on-site support, and reliable service across India.",
      },
    ],
  },
};
